import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
}
interface State {
  error: Error | null;
}

/** Catches render-time errors anywhere in the admin console and shows a branded
 * fallback instead of a blank screen. */
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('Unhandled admin UI error:', error, info.componentStack);
  }

  override render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-5 bg-neutral-950 px-6 text-center">
        <div className="flex items-center gap-2.5">
          <svg width="28" height="28" viewBox="0 0 32 32" fill="none" aria-hidden="true">
            <rect width="32" height="32" rx="8" fill="#0B0F0E" />
            <rect x="7" y="8" width="18" height="4.5" rx="2.25" fill="#34D399" />
            <rect x="7" y="13.75" width="13" height="4.5" rx="2.25" fill="#FBBF24" />
            <rect x="7" y="19.5" width="8" height="4.5" rx="2.25" fill="#FB7185" />
          </svg>
          <span className="text-lg font-bold tracking-tight text-white">
            Commission<span className="text-emerald-400">Sight</span>
            <span className="ml-2 rounded-full bg-white/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-300 ring-1 ring-white/15">
              Admin
            </span>
          </span>
        </div>
        <div className="max-w-md">
          <h1 className="text-2xl font-bold tracking-tight text-white">Something went wrong</h1>
          <p className="mt-2 text-sm text-neutral-400">
            An unexpected error interrupted the console. Reloading usually fixes it.
          </p>
          <p className="mt-3 break-words rounded-lg bg-neutral-900 px-3 py-2 text-left font-mono text-xs text-neutral-400">
            {error.message}
          </p>
        </div>
        <button
          type="button"
          onClick={() => {
            window.location.reload();
          }}
          className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-emerald-700"
        >
          Reload console
        </button>
      </div>
    );
  }
}
